import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CreditCard, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BILLING_OVERVIEW_KEY, billingApi } from '@/lib/billing-api';
import { getErrorMessage } from '@/lib/errors';

/**
 * How long to keep asking before saying so.
 *
 * A gateway's webhook usually arrives within a second or two, but it is a separate HTTP request
 * from a third party and nothing guarantees it beats the customer's browser back to us. Thirty
 * seconds is generous for the normal case and short enough that somebody is not left watching a
 * spinner wondering whether their card was charged.
 */
const CONFIRM_FOR_SECONDS = 30;
const POLL_MS = 2000;

/**
 * Where the gateway returns a customer after paying.
 *
 * ## Why this page exists
 *
 * It did not, and `BillingConfig::checkoutSuccessUrl()` has always pointed here. Every customer
 * who completed a payment landed on the 404 page — the worst possible moment for one, because they
 * have just been charged and have no idea whether it worked.
 *
 * ## It does not claim success on arrival
 *
 * Being redirected here means the gateway finished with the customer, not that the platform knows
 * about it. Activation happens when the webhook arrives, which is a separate request from a third
 * party and may land after the browser does. Saying "you're subscribed" before the subscription
 * exists would be a guess that is wrong often enough to matter.
 *
 * So it polls, and says which of the three states it is actually in: confirming, confirmed, or
 * taking longer than expected. The last is not an error — the payment is made and the webhook will
 * arrive — so it says that rather than implying something went wrong.
 */
export function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const subscriptionId = params.get('subscription') ?? '';
  const [elapsed, setElapsed] = useState(0);

  const overview = useQuery({
    queryKey: BILLING_OVERVIEW_KEY,
    queryFn: billingApi.overview,
    // Stop as soon as it is live, or once the window closes — a page left open must not poll for
    // the rest of the day.
    refetchInterval: (query) => {
      const data = query.state.data;
      // `grants_access` rather than a status string: it is the platform's own verdict, computed
      // from the clock as well as the stored status. Matching on `status` is what once put an
      // "Active" badge on a subscription whose term had ended.
      const live = data?.subscriptions.some((s) => s.id === subscriptionId && s.grants_access);

      return live === true || elapsed >= CONFIRM_FOR_SECONDS ? false : POLL_MS;
    },
  });

  useEffect(() => {
    const timer = setInterval(() => setElapsed((seconds) => seconds + 1), 1000);

    return () => clearInterval(timer);
  }, []);

  // Everything downstream of a subscription — quotas, the dashboard's counts, whether a control is
  // disabled — is derived from it. Left stale, the customer pays and then finds the product still
  // telling them they have no plan.
  useEffect(() => {
    return () => {
      void queryClient.invalidateQueries({ queryKey: BILLING_OVERVIEW_KEY });
    };
  }, [queryClient]);

  const subscription = overview.data?.subscriptions.find((s) => s.id === subscriptionId);
  const live = subscription?.grants_access === true;
  const stillWaiting = !live && elapsed < CONFIRM_FOR_SECONDS;

  return (
    <div className="mx-auto max-w-2xl space-y-6 py-10">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {live ? (
              <CheckCircle2 className="size-5 text-emerald-500" />
            ) : (
              <CreditCard className="size-5" />
            )}
            {live ? 'You’re all set' : 'Confirming your payment'}
          </CardTitle>
          <CardDescription>
            {live
              ? 'Your subscription is active and everything in your plan is available now.'
              : 'Your payment went through. We’re waiting for the payment provider to confirm it.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {overview.isError && <Alert variant="danger">{getErrorMessage(overview.error)}</Alert>}

          {stillWaiting && (
            <p className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 className="size-4 animate-spin" />
              This usually takes a couple of seconds.
            </p>
          )}

          {/* Not an error state, and phrased so it does not read as one. The money has been taken
              and the webhook will arrive; what has failed is this page's patience, not the
              payment. */}
          {!live && elapsed >= CONFIRM_FOR_SECONDS && (
            <Alert variant="info">
              Confirmation is taking longer than usual. Your payment has been taken and your
              subscription will activate as soon as the provider confirms it. You do not need to
              pay again. If it has not appeared on your billing page within a few minutes, contact
              support and quote your payment receipt.
            </Alert>
          )}

          {live && subscription !== undefined && (
            <p className="text-sm">
              <span className="text-muted-foreground">Now on: </span>
              {subscription.price_formatted} {subscription.currency} ·{' '}
              {subscription.billing_interval_label}
            </p>
          )}

          {/* A plan that starts during the free trial ends it, so the trial badge and its limits go
              away at this moment. Said here so that disappearing is expected rather than alarming. */}
          {live && overview.data?.trial.status === 'converted' && (
            <p className="text-sm text-muted-foreground">
              Your free trial has ended now that this plan is active. Everything you set up during the
              trial carries on as it is, with this plan&rsquo;s limits.
            </p>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button asChild>
              <Link to="/dashboard">Go to your dashboard</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/billing">View billing</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
